import { COMPLEXITY_AXES, type Complexity } from '@/lib/types';
import { cx } from '@/lib/format';
import { useI18n } from '@/store/preferences';

export const COMPLEXITY_MAX = COMPLEXITY_AXES.length * 5;

export function complexityTotal(complexity: Complexity): number {
  return COMPLEXITY_AXES.reduce((sum, axis) => sum + (complexity[axis] ?? 0), 0);
}

const TICKS = [1, 2, 3, 4, 5];

const SIZES = {
  sm: { tick: 'h-2.5 w-[3px]', gap: 'gap-[2px]', block: 'gap-[9px]' },
  md: { tick: 'h-3.5 w-[4px]', gap: 'gap-[3px]', block: 'gap-[10px]' },
  lg: { tick: 'h-6 w-[5px]', gap: 'gap-[4px]', block: 'gap-3' },
} as const;

/**
 * Twenty-five ticks in five blocks: one block per axis, one tick per point.
 * Reading it sideways gives the total; reading a block gives where the work was.
 */
export function ComplexityMeter({
  complexity,
  size = 'sm',
  animate = false,
  showTotal = true,
  className,
}: {
  complexity: Complexity;
  size?: keyof typeof SIZES;
  animate?: boolean;
  showTotal?: boolean;
  className?: string;
}) {
  const { t } = useI18n();
  const s = SIZES[size];
  const total = complexityTotal(complexity);

  const label = COMPLEXITY_AXES.map((axis) => `${t.complexity[axis]} ${complexity[axis]}/5`).join(
    ', '
  );

  return (
    <span className={cx('inline-flex items-center', s.block, className)}>
      <span
        className={cx('inline-flex items-center', s.block)}
        role="img"
        aria-label={`${t.complexity.total} ${total}/${COMPLEXITY_MAX} — ${label}`}
      >
        {COMPLEXITY_AXES.map((axis, blockIndex) => (
          <span key={axis} className={cx('inline-flex items-end', s.gap)}>
            {TICKS.map((tick) => {
              const on = tick <= (complexity[axis] ?? 0);
              return (
                <i
                  key={tick}
                  className={cx(
                    'block not-italic transition-colors duration-300',
                    s.tick,
                    on ? 'bg-signal' : 'bg-ink/15',
                    animate && 'origin-bottom animate-sweep-in'
                  )}
                  style={
                    animate
                      ? { animationDelay: `${blockIndex * 70 + tick * 22}ms` }
                      : undefined
                  }
                />
              );
            })}
          </span>
        ))}
      </span>

      {showTotal && (
        <span className="font-mono text-2xs tabular text-signal">
          {total}
          <span className="text-faint">/{COMPLEXITY_MAX}</span>
        </span>
      )}
    </span>
  );
}

/** Expanded readout: the same strip, with every axis named and explained. */
export function ComplexityPlate({ complexity }: { complexity: Complexity }) {
  const { t } = useI18n();
  const total = complexityTotal(complexity);

  return (
    <section className="plate">
      <header className="plate-head">
        <p className="eyebrow">{t.complexity.title}</p>
        <p className="font-mono text-2xs tabular text-faint">
          <span className="text-signal">{total}</span>/{COMPLEXITY_MAX}
        </p>
      </header>

      <div className="p-4 sm:p-5">
        <ul className="space-y-3.5">
          {COMPLEXITY_AXES.map((axis) => (
            <li key={axis} className="grid gap-1.5 sm:grid-cols-[9rem_auto_minmax(0,1fr)] sm:items-baseline sm:gap-4">
              <p className="text-xs font-medium text-ink">
                {t.complexity[axis]}
              </p>

              <span
                className="inline-flex items-end gap-[3px]"
                role="img"
                aria-label={`${complexity[axis]}/5`}
              >
                {TICKS.map((tick) => (
                  <i
                    key={tick}
                    className={cx(
                      'block h-3.5 w-[4px] not-italic',
                      tick <= (complexity[axis] ?? 0) ? 'bg-signal' : 'bg-ink/15'
                    )}
                  />
                ))}
              </span>

              <p className="text-xs leading-relaxed text-faint">
                {t.complexity[`${axis}Hint` as const]}
              </p>
            </li>
          ))}
        </ul>

        <p className="mt-5 border-t border-line pt-4 text-xs leading-relaxed text-faint">
          {t.complexity.basis}
        </p>
      </div>
    </section>
  );
}
